import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from "react";
import api from '../adapters/api';
import { getWsUrl } from '../utils/wsUrl';

import { AuthContext } from "../context/AuthContext";

// สร้าง Context
export const CartContext = createContext();

// Provider Component
export const CartProvider = ({ children }) => {
  const [cartGuid, setCartGuid] = useState(null);
  const [numberOfCartItems, setNumberOfCartItems] = useState(0);
  const [cart, setCart] = useState([]);
  const [sumCart, setSumCart] = useState(0);
  const [priceLevel, setPriceLevel] = useState(0);
  const [currentOrderNo, setCurrentOrderNo] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [customerName, setCustomerName] = useState(null);
  const [lockInfo, setLockInfo] = useState(null);
  const { currentUser } = useContext(AuthContext);
  const wsRef = useRef(null);
  const lockListeners = useRef([]);
  const notificationListeners = useRef([]);
  const systemListeners = useRef([]);

const reconnectDelay = useRef(1000);
const reconnectTimer = useRef(null);
const intentionalClose = useRef(false);

useEffect(() => {
  if (!currentUser?.user_code) return;

  const wsUrl = getWsUrl();

  let everOpened = false;

  const connect = () => {
    const socket = new WebSocket(wsUrl);
    wsRef.current = socket;

    socket.onopen = () => {
      reconnectDelay.current = 1000;
      // On reconnect, restore full cart state and resync anything broadcast while we were offline
      if (everOpened) {
        fetchCartHeader();
        fetchCart();
        fetchNumberOfCartItems();
        fetchSumCart();
        lockListeners.current.forEach(fn => fn());
        notificationListeners.current.forEach(fn => fn({ type: 'DRAFT_LIST_UPDATED' }));
        systemListeners.current.forEach(fn => fn({ type: 'WS_RECONNECTED' }));
      }
      everOpened = true;
    };

    socket.onmessage = async (event) => {
      try {
        let message;
        if (event.data instanceof Blob) {
          const text = await event.data.text();
          message = JSON.parse(text);
        } else {
          message = JSON.parse(event.data);
        }

        if (message.type === "CART_UPDATED") {
          fetchCart();
          fetchNumberOfCartItems();
          fetchSumCart();
        }
        if (message.type === "LOCK_UPDATED") {
          lockListeners.current.forEach(fn => fn());
        }
        if (message.type === "DRAFT_NOTIFICATION" || message.type === "DRAFT_LIST_UPDATED") {
          notificationListeners.current.forEach(fn => fn(message));
        }
        if (typeof message.type === "string" && message.type.startsWith("RESTART_")) {
          systemListeners.current.forEach(fn => fn(message));
        }
      } catch (err) {
        console.error("❌ Error parsing message:", err);
      }
    };

    socket.onclose = () => {
      if (wsRef.current === socket) wsRef.current = null;
      if (intentionalClose.current) return;
      // Keep retrying with backoff (cap 30s); tab focus / network return reconnects immediately
      const delay = reconnectDelay.current;
      reconnectDelay.current = Math.min(delay * 2, 30000);
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = setTimeout(connect, delay);
    };

    socket.onerror = (err) => {
      console.error("❌ WebSocket error:", err);
    };
  };

  // Reconnect right away when the user comes back, instead of waiting out the backoff
  const reconnectNow = () => {
    if (intentionalClose.current) return;
    if (document.visibilityState !== 'visible') return;
    const s = wsRef.current;
    if (s && (s.readyState === WebSocket.OPEN || s.readyState === WebSocket.CONNECTING)) return;
    clearTimeout(reconnectTimer.current);
    reconnectDelay.current = 1000;
    connect();
  };

  intentionalClose.current = false;
  connect();
  document.addEventListener('visibilitychange', reconnectNow);
  window.addEventListener('online', reconnectNow);
  window.addEventListener('focus', reconnectNow);

  return () => {
    intentionalClose.current = true;
    document.removeEventListener('visibilitychange', reconnectNow);
    window.removeEventListener('online', reconnectNow);
    window.removeEventListener('focus', reconnectNow);
    clearTimeout(reconnectTimer.current);
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };
}, [currentUser]);

const subscribeToLockUpdates = useCallback((fn) => {
  lockListeners.current.push(fn);
  return () => {
    lockListeners.current = lockListeners.current.filter(f => f !== fn);
  };
}, []);

const addNotificationListener = useCallback((fn) => {
  notificationListeners.current.push(fn);
  return () => {
    notificationListeners.current = notificationListeners.current.filter(f => f !== fn);
  };
}, []);

// RESTART_* broadcasts from backend/routes/system.js, plus WS_RECONNECTED after the socket comes back
const addSystemListener = useCallback((fn) => {
  systemListeners.current.push(fn);
  return () => {
    systemListeners.current = systemListeners.current.filter(f => f !== fn);
  };
}, []);

const loadOrderIntoCart = async (docNo) => {
  if (!currentUser) return;
  try {
    const { data } = await api.post('/api/loadOrderIntoCart', { docNo }, { withCredentials: true });
    setCartGuid(data.cartGuid);
    setCurrentOrderNo(docNo);
    await fetchCart();
    await fetchNumberOfCartItems();
    await fetchSumCart();
    await fetchCartHeader();
  } catch (error) {
    console.error('❌ Error loading order into cart:', error);
    // Rethrow so the caller can show an error instead of opening an empty checkout
    throw error;
  }
};


const fetchCartHeader = async (overrideGuid = null) => {
  try {
    // ถ้า overrideGuid มี ให้ใช้เลย เพื่อข้าม createCartIfNotExist()
    let activeGuid = overrideGuid;
    if (!activeGuid) {
      activeGuid = await createCartIfNotExist();
    }

    // 2) เรียก API ดึง header ของ cart ที่ active อยู่ (ใช้ activeGuid เพื่อให้ชัวร์)
    const res = await api.post(
      "/api/getDefaultCartHeader",
      { user: currentUser.user_code },
      { withCredentials: true }
    );
    const header = res.data || {};

    // … จัดการ header ตรงนี้เหมือนเดิม
    const ref = header.ref_doc_no || null;
    setCurrentOrderNo(ref);

    // Restore lockInfo after refresh if this bill is still locked by the current user.
    // GUID format: <25 random chars>_<user_code> — user_code starts at index 26.
    if (ref && header.bill_guid_code) {
      const lockUser = header.bill_guid_code.substring(26);
      if (lockUser === currentUser?.user_code) {
        setLockInfo({ doc_no: ref, guid: header.bill_guid_code });
      }
    }

    setPriceLevel(header.price_level ?? 0);
    setCustomerId(header.cust_code || null);
    setCustomerName(header.cust_name || null);
    fetchCart();
    fetchNumberOfCartItems();
    fetchSumCart();
  } catch (err) {
    console.error("Error fetching cart header:", err);
  }
};

  // ดึงจำนวนสินค้าทั้งหมดในตะกร้า
  const fetchNumberOfCartItems = async () => {
    try {
      const res = await api.post("/api/getNumberOfCartItems");
      setNumberOfCartItems(res.data.totalItems);
    } catch (error) {
      console.error("Error fetching cart item count:", error);
    }
  };

  // ดึงข้อมูลตะกร้าสินค้า
  const fetchCart = async () => {
    try {
      const res = await api.post("/api/cart");
      setCart(res.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  };

  // ดึงยอดรวมตะกร้าสินค้า
  const fetchSumCart = async () => {
    try {
      const res = await api.post("/api/sumCart");
      setSumCart(res.data);
    } catch (error) {
      console.error("Error fetching cart sum:", error);
    }
  };


  // เพิ่มจำนวนสินค้า 1 ชิ้นในตะกร้า
  const addOneItemToCart = async (roworder, unitPrice) => {
    try {
      await api.post(
        '/api/addOneItemToCart',
        { roworder, unitPrice },
        { withCredentials: true }
      );
      fetchCart();
      fetchNumberOfCartItems();
      fetchSumCart();
    } catch (error) {
      console.error('Error adding one item:', error);
    }
  };

  // ลดจำนวนสินค้า 1 ชิ้นในตะกร้า
  const removeOneItemFromCart = async (roworder, unitPrice) => {
    try {
      await api.post(
        '/api/removeOneItemFromCart',
        { cartGuid, roworder, unitPrice },
        { withCredentials: true }
      );
      fetchCart();
      fetchNumberOfCartItems();
      fetchSumCart();
    } catch (error) {
      console.error('Error removing one item:', error);
    }
  };

  // ลบสินค้าออกจากตะกร้า
  const deleteItemFromCart = async (roworder) => {
    try {
      await api.post(
        '/api/deleteItemFromCart',
        { roworder },
        { withCredentials: true }
      );
      fetchCart();
      fetchNumberOfCartItems();
      fetchSumCart();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const createCartLockRef = useRef(null);

  const createCartIfNotExist = async () => {
    if (cartGuid) return cartGuid;

    if (createCartLockRef.current) return createCartLockRef.current;

    let resolve, reject;
    createCartLockRef.current = new Promise((res, rej) => { resolve = res; reject = rej; });

    try {
      const res = await api.post("/api/getActiveCartGuid");
      if (res.data?.cartGuid) {
        setCartGuid(res.data.cartGuid);
        resolve(res.data.cartGuid);
        return res.data.cartGuid;
      }

      const response = await api.post("/api/createCart");
      const newGuid = response.data.cartGuid;
      setCartGuid(newGuid);
      resolve(newGuid);
      return newGuid;
    } catch (err) {
      // Reject the shared promise so any concurrent waiters fail fast instead of hanging
      reject(err);
      throw err;
    } finally {
      createCartLockRef.current = null;
    }
  };
  

  // เพิ่มสินค้าใหม่ลงในตะกร้า
  // ขอ guid ของตะกร้า active จาก server ใหม่ (ไม่ใช้ค่าใน state) — สร้างใหม่ถ้าไม่มี
  const refreshActiveCartGuid = async () => {
    const res = await api.post("/api/getActiveCartGuid");
    let guid = res.data?.cartGuid;
    if (!guid) {
      const response = await api.post("/api/createCart");
      guid = response.data.cartGuid;
    }
    setCartGuid(guid);
    return guid;
  };

  const addToCart = async ({ code, name, quantity, unit, unitPrice }) => {
    const currentGuid = await createCartIfNotExist();
    const payload = { code, name, quantity, unit, unitPrice };
    try {
      try {
        await api.post("/api/addToCart", { cartGuid: currentGuid, ...payload });
      } catch (err) {
        // 403 = cartGuid ใน state ไม่ใช่ตะกร้า active แล้ว (เช่นถูกบันทึกเป็นดราฟต์/ลบจากแท็บอื่น)
        if (err.response?.status !== 403) throw err;
        const freshGuid = await refreshActiveCartGuid();
        await api.post("/api/addToCart", { cartGuid: freshGuid, ...payload });
      }
      fetchCartHeader();
    } catch (error) {
      console.error("Error adding to cart:", error);
      throw error;
    }
  };

  // const clearCart = async () => {
  //   try {
  //     // เรียก endpoint /clearCart
  //     const res = await api.post("/clearCart", null, { withCredentials: true });
  //     const { newCartGuid } = res.data;

  //     // เซ็ต cartGuid ใหม่ที่ได้จาก server
  //     setCartGuid(newCartGuid);

  //     // รีเฟรชข้อมูลตะกร้า (ตอนนี้น่าจะว่าง) และสรุปค่าต่าง ๆ
  //     fetchCart();
  //     fetchNumberOfCartItems();
  //     fetchSumCart();

  //     // อาจจะต้องดึง cart header (priceLevel) เข้าใหม่ หากใช้ในการคำนวณราคา
  //     fetchCartHeader();
  //   } catch (error) {
  //     console.error("❌ Error clearing cart:", error);
  //   }
  // };

  const deleteCart = async () => {
    try {
      await api.post("/api/deleteCart", null, { withCredentials: true });
    } catch (err) {
      // ถ้าสถานะ 404 ให้ถือว่ายอมรับได้ (เพราะแปลว่าไม่มี cart active ให้ลบ)
      if (err.response?.status === 404) {
        console.warn("ไม่มีตะกร้า active ให้ลบ (404) แต่จะข้ามไปต่อ");
      } else {
        console.error("❌ Error deleting cart:", err);
      }
    } finally {
      setCartGuid(null);
      setCart([]);
      setNumberOfCartItems(0);
      setSumCart(0);
      setCurrentOrderNo(null);
      setCustomerId(null);
      setCustomerName(null);
      setPriceLevel(0);

      // รีเฟรช badge กันต่อ
      await fetchNumberOfCartItems();
      await fetchSumCart();
    }
  };

  // อัปเดตจำนวนสินค้าในตะกร้า
  const updateQtyItemInCart = async ({ roworder, quantity }) => {
    try {
      await api.post("/api/updateQtyItemInCart", { roworder, quantity });
      fetchCart(); fetchNumberOfCartItems(); fetchSumCart();
    } catch (error) {
      console.error("Error updating qty in cart:", error);
      throw error; // ให้หน้าสินค้าแจ้ง "ไม่สามารถเพิ่มสินค้าลงตะกร้าได้" แทนการแจ้งว่าสำเร็จ
    }
  };

  const updateDetailsItemInCart = async (newRow) => {
    const { roworder, item_name, unit_code, qty, unitPrice, line_number } = newRow;
    if (!roworder) return;
    try {
      await api.post("/api/updateDetailsItemInCart", {
        cartGuid, roworder, item_name, unit_code, qty, unitPrice, line_number
      });
      fetchCart(); fetchNumberOfCartItems(); fetchSumCart();
    } catch (error) {
      console.error("Error updating cart item:", error);
      fetchCart(); // server ไม่รับ (เช่นจำนวน ≤ 0) → ดึงค่าจริงกลับมาแทนค่าที่แสดงค้างไว้
    }
  };
  
  const resetCartState = () => {
    setCartGuid(null);
    setCart([]);
    setNumberOfCartItems(0);
    setSumCart(0);
    setCurrentOrderNo(null);
    setCustomerId(null);
    setCustomerName(null);
    setPriceLevel(0);
    setLockInfo(null);
  };

  const setCartGuidActive = async (targetGuid) => {
    const res = await api.post("/api/setCartActive", { cartGuid: targetGuid });
    const guid = res.data.cartGuid;
    setCartGuid(guid);
    return guid;
  };

  useEffect(() => {
    if (!currentUser?.user_code) return;
    // เริ่มต้นให้มี cart ใหม่ ถ้าไม่มี active cartGuid
    createCartIfNotExist().then(() => {
      fetchCart();       
      fetchNumberOfCartItems();
      fetchSumCart();
      fetchCartHeader();
    });
  }, [currentUser]);


  return (
    <CartContext.Provider
      value={{
        cartGuid,
        setCartGuid,
        setCartGuidActive,
        numberOfCartItems,
        cart,
        sumCart,
        priceLevel,
        currentOrderNo,
        setPriceLevel,
        customerId,
        setCustomerId,
        customerName,
        setCustomerName,
        addOneItemToCart,
        removeOneItemFromCart,
        deleteItemFromCart,
        fetchCart,
        fetchSumCart,
        addToCart,
        // clearCart,
        updateQtyItemInCart,
        updateDetailsItemInCart,
        loadOrderIntoCart,
        setCurrentOrderNo,
        deleteCart,
        resetCartState,
        fetchCartHeader,
        fetchNumberOfCartItems,
        createCartIfNotExist,
        lockInfo,
        setLockInfo,
        subscribeToLockUpdates,
        addNotificationListener,
        addSystemListener,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
